import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import News from "./docs/News";
import ReportPersonInfoSidebar360 from "./ReportPersonInfoSidebar360";
import {
  ChevronLeftIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Reports360SummaryEditor, {
  Reports360SummaryEditorHandle,
} from "./docs/Reports360SummaryEditor";
import Reports360NotesAutosave from "./docs/Reports360NotesAutosave";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useFamaFlags } from "../api/FamaFlags";
import LegalCase from "./docs/LegalCase";
import Diligence from "./docs/Diligence";
import { useReportByUUID, useReportReruns } from "../api/ReportsApi";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { useGetPersonAccounts, useGetPersonInfo } from "../api/PersonApi";
import { PersonInfoApi } from "../api/Person";
import { useGetPersonConsentForms } from "../api/360FilesAPI";
import AddNoteIcon from "../shared-components/icons/AddNoteIcon";
import { useGetSubworkflowData } from "../api/WorkflowApi";
import { WORKFLOW_STEP_STATUS_ENUM } from "../api/Workflows";
import { useRegeneratePDF, useCheckPDF } from "../api/ReportsApi";
import MisconductLink from "./docs/MisconductLink";
import { useGetComputedBlueprintFromWorkflow } from "../api/BlueprintsApi";
import { useGetCompanyBase } from "../api/CompaniesApi";
import { ThreeSixtyItemTypes } from "../api/Items";
import {
  ConfirmationDialogIcon,
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import { ButtonColorTypes } from "../shared-components/Button";
import { BooleanSearchTab } from "./BooleanSearchTab";
import WebSearchDoneButton from "./WebSearchDone";
import { useLatestWebSearchJobForReport } from "../api/WebSearchAPI";
import { PreventLeaveProvider } from "../utils/PreventLeaveContext";
import { classNames, LONG_TEXT_WRAP_CLASSES } from "../utils";
import {
  isWebSearchWorkbenchEntry,
  shouldOpenWebSearchWorkbenchTab,
} from "./workbenchEntrySource";
import { CopyFromIcon } from "../shared-components/icons";
import { useAppContext } from "../AppProviders";
import { API_URL } from "../api/helpers";
import { NewsItem } from "./docs/News";
import { LegalCaseItem } from "./docs/LegalCase";
import { DiligenceItem } from "./docs/Diligence";
import CopyFromReportModal from "./CopyFromReportModal";
import { getCopyFromReportSections } from "./copyFromReportSections";
import RerunReportBanner from "./RerunReportBanner";
import { isRerunReport } from "../reports/reportRerunUtils";
import { useWorkbenchClaimPrompt } from "./useWorkbenchClaimPrompt";
import { WorkbenchClaimPromptDialog } from "./WorkbenchClaimPromptDialog";
import {
  get360SubworkflowRunUuid,
  selectRunsForParent,
} from "./reportAssignment";

const REPORT_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidReportIdFormat(value: string): boolean {
  return REPORT_UUID_PATTERN.test(value.trim());
}

const COPY_FROM_REPORT_LOAD_ERROR =
  "We couldn't load that report. Check the report UUID and try again.";

const COPY_FROM_REPORT_SAME_AS_CURRENT_ERROR =
  "The source report cannot be the same as the report you are editing. Enter a different report ID.";

const defaultItemVisibility = {
  showDiligence: true,
  showNews: true,
  showLegal: true,
  showFamaReport: true,
};

type WorkbenchTab = "investigator" | "summary" | "webSearch";

const workbenchTabs: { id: WorkbenchTab; label: string }[] = [
  { id: "investigator", label: "Investigator's Notes" },
  { id: "summary", label: "Summary" },
  { id: "webSearch", label: "Web Search" },
];

const Workbench360: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [reportId, setReportId] = useState<string>("");
  const [isReady, setIsReady] = useState<boolean>(false);
  const isQAMode = location.pathname.includes("/qa-workbench");
  const isWebSearchWorkbenchEntryRoute = isWebSearchWorkbenchEntry(
    location.state
  );
  const shouldOpenWebSearchTab = shouldOpenWebSearchWorkbenchTab(
    location.state
  );
  const {
    canAddNotes,
    canViewWIP360Reports,
    canEdit360Qa,
    canNavAmendment,
    canView360,
    canEdit360Reports,
  } = usePermissionValidator();
  const { v3DiligenceEnabled } = useFamaFlags();
  const [itemVisibility, setItemVisibility] = useState<{
    showDiligence: boolean;
    showNews: boolean;
    showLegal: boolean;
    showFamaReport: boolean;
  } | null>(null);
  const [activeWorkbenchTab, setActiveWorkbenchTab] = useState<WorkbenchTab>(
    () => (shouldOpenWebSearchTab ? "webSearch" : "investigator")
  );
  const [tabStatuses, setTabStatuses] = useState<Record<WorkbenchTab, string>>({
    investigator: "",
    summary: "",
    webSearch: "",
  });
  const [isSummaryEditing, setIsSummaryEditing] = useState<boolean>(false);
  const summaryEditorRef = useRef<Reports360SummaryEditorHandle | null>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const {
    modal: leaveConfirmationModal,
    show: showLeaveConfirmationModal,
    hide: hideLeaveConfirmationModal,
  } = useConfirmationDialog();
  const {
    data: reportData,
    isLoading: reportDataLoading,
    mutate: reportDataGet,
  } = useReportByUUID(undefined, true);
  const {
    data: subworkflowData,
    mutate: subworkflowDataGet,
    variables: subworkflowParentUuid,
    reset: resetSubworkflowData,
  } = useGetSubworkflowData();
  /**
   * Children of the active report's workflow run, or undefined while the loaded batch belongs to a
   * different report. Filtering here instead of clearing the fetch keeps a response that lands
   * during an unrelated re-render (e.g. a failed request elsewhere on the page) from being lost.
   */
  const subworkflowRuns = useMemo(() => {
    if (!reportData || reportData.report_uuid !== reportId) {
      return undefined;
    }
    return selectRunsForParent(
      subworkflowData,
      subworkflowParentUuid,
      reportData.workflow_run_uuid
    );
  }, [reportData, reportId, subworkflowData, subworkflowParentUuid]);
  const subworkflow360 = useMemo(
    () => get360SubworkflowRunUuid(subworkflowRuns) ?? "",
    [subworkflowRuns]
  );
  const isOnWebSearchStep = !!subworkflowRuns?.some(
    (wr) => wr.last_running_step === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch
  );
  const { isClaimPromptOpen, isClaiming, handleClaimPromptAction } =
    useWorkbenchClaimPrompt({
      reportId,
      reportData,
      reportDataLoading,
      claimWorkflowRunUuid: subworkflow360 || undefined,
      refreshReport: reportDataGet,
    });
  const { reportReruns } = useReportReruns(reportId);
  const {
    data: company,
    mutate: getCompanyBase,
    isLoading: companyLoading,
    isSuccess: companyFetchSucceeded,
    variables: fetchedCompanyUUID,
  } = useGetCompanyBase();
  const companyUUID = reportData?.company_uuid;
  useEffect(() => {
    if (companyUUID) {
      getCompanyBase(companyUUID);
    }
  }, [companyUUID, getCompanyBase]);
  const companyMatchesReport =
    !!companyUUID &&
    fetchedCompanyUUID === companyUUID &&
    !companyLoading &&
    companyFetchSucceeded;
  const companyNote = companyMatchesReport ? company?.notes : undefined;
  const isAmendment360 =
    (location.state as { amendment360?: boolean } | null)?.amendment360 ===
      true ||
    reportData?.step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyAmend;

  const showRerunBanner = useMemo(
    () =>
      reportData?.report_uuid === reportId &&
      isRerunReport(reportData, reportReruns),
    [reportData, reportId, reportReruns]
  );
  const {
    data: personEx,
    isLoading: personExLoading,
    mutate: personExGet,
  } = useGetPersonInfo();
  const {
    data: personAccounts,
    isLoading: personAccountsLoading,
    mutate: personAccountsGet,
  } = useGetPersonAccounts();
  const [personUuid, setPersonUuid] = useState<string | undefined>();
  /** Optimistic override for person info when user saves edits (no persistence yet). */
  const [personOverride, setPersonOverride] = useState<PersonInfoApi | null>(
    null
  );
  const { data: personConsentForms, isLoading: personConsentFormsLoading } =
    useGetPersonConsentForms(personUuid ?? "");
  const [webSearchRunExternalId, setWebSearchRunExternalId] = useState<
    string | null
  >(null);
  const [pdfExists, setPdfExists] = useState<boolean>(false);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [showCopyFromModal, setShowCopyFromModal] = useState<boolean>(false);
  const [sourceReportUuid, setSourceReportUuid] = useState<string>("");
  const [sourceNewsItems, setSourceNewsItems] = useState<NewsItem[]>([]);
  const [sourceLegalItems, setSourceLegalItems] = useState<LegalCaseItem[]>([]);
  const [sourceDiligenceItems, setSourceDiligenceItems] = useState<
    DiligenceItem[]
  >([]);
  const [selectedNewsIds, setSelectedNewsIds] = useState<string[]>([]);
  const [selectedLegalIds, setSelectedLegalIds] = useState<string[]>([]);
  const [selectedDiligenceIds, setSelectedDiligenceIds] = useState<string[]>(
    []
  );
  const [isCopySummaryStep, setIsCopySummaryStep] = useState<boolean>(false);
  const [loadCopySourceError, setLoadCopySourceError] = useState<string>("");
  const [isLoadingCopySource, setIsLoadingCopySource] =
    useState<boolean>(false);
  const [isCopyingItems, setIsCopyingItems] = useState<boolean>(false);
  const copyFromSections = React.useMemo(
    () =>
      getCopyFromReportSections(
        itemVisibility,
        isWebSearchWorkbenchEntryRoute,
        v3DiligenceEnabled
      ),
    [itemVisibility, isWebSearchWorkbenchEntryRoute, v3DiligenceEnabled]
  );
  const { mutate: regeneratePDF, isLoading: isGeneratingPDF } =
    useRegeneratePDF();
  const { mutate: checkPDF } = useCheckPDF();
  const { requestApi } = useAppContext();
  const {
    data: computedBlueprint,
    mutate: getComputedBlueprint,
    isLoading: isComputedBlueprintLoading,
    isError: isComputedBlueprintError,
    isSuccess: isComputedBlueprintSuccess,
  } = useGetComputedBlueprintFromWorkflow();

  // Keep a stable reference to the mutate function to avoid dependency loops
  const checkPDFRef = useRef(checkPDF);
  useEffect(() => {
    checkPDFRef.current = checkPDF;
  }, [checkPDF]);

  const requestLeaveConfirmation = useCallback(
    (action: () => void) => {
      pendingActionRef.current = action;
      showLeaveConfirmationModal(
        "Leave without Saving?",
        "Are you sure you want to leave the summary without saving? All work will be lost and time will not be tracked.",
        (dialogAction: ConfirmDialogActions) => {
          if (dialogAction !== "continue") {
            return;
          }
          hideLeaveConfirmationModal();
          summaryEditorRef.current?.discardChanges();
          pendingActionRef.current?.();
        },
        undefined,
        true,
        "Cancel",
        ButtonColorTypes.cancel,
        "Yes, Leave",
        ButtonColorTypes.warning,
        ConfirmationDialogIcon.WARN
      );
    },
    [showLeaveConfirmationModal, hideLeaveConfirmationModal]
  );

  const runGeneratePDF = () => {
    if (!reportId) return;
    if (pdfExists) {
      const reviewPath = isQAMode
        ? `/360/${reportId}/qa-review`
        : `/360/${reportId}/review`;
      navigate(reviewPath, {
        state: {
          reportData,
          subworkflowData: subworkflowRuns,
          ...(isAmendment360 && { amendment360: true }),
        },
      });
      return;
    }
    regeneratePDF(reportId);
    setIsPolling(true);
    setPdfExists(false);
  };

  const handleGeneratePDF = () => {
    const willNavigate =
      Boolean(reportId) && pdfExists && !isGeneratingPDF && !isPolling;
    if (willNavigate && isSummaryEditing) {
      requestLeaveConfirmation(runGeneratePDF);
      return;
    }
    runGeneratePDF();
  };

  const handleRegeneratePDF = () => {
    if (reportId) {
      regeneratePDF(reportId);
      setIsPolling(true);
      setPdfExists(false);
    }
  };

  const handleNavigateBack = () => {
    if (isAmendment360) {
      navigate("/reports/amendment");
      return;
    }
    let destination = isQAMode ? "/360/qa" : "/360/investigating";
    if (isOnWebSearchStep) {
      destination = "/360/web_search";
    }

    const executeNavigation = () => navigate(destination);
    if (isSummaryEditing) {
      requestLeaveConfirmation(executeNavigation);
      return;
    }
    executeNavigation();
  };

  const handleCloseCopyFromModal = () => {
    if (isLoadingCopySource || isCopyingItems) {
      return;
    }
    clearCopyFromModalState();
    setShowCopyFromModal(false);
  };

  /** Clears items loaded for copy and selection state; call when source report ID changes or load fails validation. */
  const clearLoadedCopySourceData = () => {
    setSourceNewsItems([]);
    setSourceLegalItems([]);
    setSourceDiligenceItems([]);
    setSelectedNewsIds([]);
    setSelectedLegalIds([]);
    setSelectedDiligenceIds([]);
    setIsCopySummaryStep(false);
  };

  const clearCopyFromModalState = () => {
    setSourceReportUuid("");
    clearLoadedCopySourceData();
    setLoadCopySourceError("");
  };

  const handleSourceReportIdChange = (value: string) => {
    setSourceReportUuid(value);
    setLoadCopySourceError("");
    clearLoadedCopySourceData();
  };

  const handleLoadCopySource = async () => {
    const trimmedId = sourceReportUuid.trim();
    if (!trimmedId) {
      clearLoadedCopySourceData();
      setLoadCopySourceError("Please enter a report ID.");
      return;
    }
    if (!isValidReportIdFormat(trimmedId)) {
      clearLoadedCopySourceData();
      setLoadCopySourceError(COPY_FROM_REPORT_LOAD_ERROR);
      return;
    }
    const currentReportId = reportId.trim().toLowerCase();
    if (currentReportId && trimmedId.toLowerCase() === currentReportId) {
      clearLoadedCopySourceData();
      setLoadCopySourceError(COPY_FROM_REPORT_SAME_AS_CURRENT_ERROR);
      return;
    }
    const { includeNews, includeLegal, includeDiligence } =
      getCopyFromReportSections(
        itemVisibility,
        isWebSearchWorkbenchEntryRoute,
        v3DiligenceEnabled
      );
    if (!includeNews && !includeLegal && !includeDiligence) {
      clearLoadedCopySourceData();
      setLoadCopySourceError(
        "There are no report item types available to copy for this workbench."
      );
      return;
    }
    setIsLoadingCopySource(true);
    setLoadCopySourceError("");
    try {
      const [loadedNews, loadedLegal, loadedDiligence] = await Promise.all([
        includeNews
          ? requestApi
              .get(`${API_URL}/itemdal/reports/${trimmedId}/news`)
              .then((response) => (response.data ?? []) as NewsItem[])
          : Promise.resolve([] as NewsItem[]),
        includeLegal
          ? requestApi
              .get(`${API_URL}/itemdal/reports/${trimmedId}/legal_cases`)
              .then((response) => (response.data ?? []) as LegalCaseItem[])
          : Promise.resolve([] as LegalCaseItem[]),
        includeDiligence
          ? requestApi
              .get(`${API_URL}/itemdal/reports/${trimmedId}/diligence`)
              .then((response) => (response.data ?? []) as DiligenceItem[])
          : Promise.resolve([] as DiligenceItem[]),
      ]);
      setSourceNewsItems(includeNews ? loadedNews : []);
      setSourceLegalItems(includeLegal ? loadedLegal : []);
      setSourceDiligenceItems(includeDiligence ? loadedDiligence : []);
      setSelectedNewsIds(
        includeNews ? loadedNews.map((item) => item.external_id) : []
      );
      setSelectedLegalIds(
        includeLegal ? loadedLegal.map((item) => item.external_id) : []
      );
      setSelectedDiligenceIds(
        includeDiligence ? loadedDiligence.map((item) => item.external_id) : []
      );
    } catch {
      clearLoadedCopySourceData();
      setLoadCopySourceError(COPY_FROM_REPORT_LOAD_ERROR);
    } finally {
      setIsLoadingCopySource(false);
    }
  };

  const toggleSelectedNews = (externalId: string) => {
    setSelectedNewsIds((prev) =>
      prev.includes(externalId)
        ? prev.filter((id) => id !== externalId)
        : [...prev, externalId]
    );
  };

  const toggleSelectedLegal = (externalId: string) => {
    setSelectedLegalIds((prev) =>
      prev.includes(externalId)
        ? prev.filter((id) => id !== externalId)
        : [...prev, externalId]
    );
  };

  const toggleSelectedDiligence = (externalId: string) => {
    setSelectedDiligenceIds((prev) =>
      prev.includes(externalId)
        ? prev.filter((id) => id !== externalId)
        : [...prev, externalId]
    );
  };

  const selectAllNews = () => {
    setSelectedNewsIds(sourceNewsItems.map((item) => item.external_id));
  };

  const unselectAllNews = () => {
    setSelectedNewsIds([]);
  };

  const selectAllLegal = () => {
    setSelectedLegalIds(sourceLegalItems.map((item) => item.external_id));
  };

  const unselectAllLegal = () => {
    setSelectedLegalIds([]);
  };

  const selectAllDiligence = () => {
    setSelectedDiligenceIds(
      sourceDiligenceItems.map((item) => item.external_id)
    );
  };

  const unselectAllDiligence = () => {
    setSelectedDiligenceIds([]);
  };

  const selectedNews = sourceNewsItems.filter((item) =>
    selectedNewsIds.includes(item.external_id)
  );
  const selectedLegal = sourceLegalItems.filter((item) =>
    selectedLegalIds.includes(item.external_id)
  );
  const selectedDiligence = sourceDiligenceItems.filter((item) =>
    selectedDiligenceIds.includes(item.external_id)
  );

  const handleProceedToCopySummary = () => {
    const trimmedSource = sourceReportUuid.trim();
    const currentReportId = reportId.trim().toLowerCase();
    if (
      trimmedSource &&
      currentReportId &&
      trimmedSource.toLowerCase() === currentReportId
    ) {
      setLoadCopySourceError(COPY_FROM_REPORT_SAME_AS_CURRENT_ERROR);
      return;
    }
    const { includeNews, includeLegal, includeDiligence } =
      getCopyFromReportSections(
        itemVisibility,
        isWebSearchWorkbenchEntryRoute,
        v3DiligenceEnabled
      );
    const hasNewsSelection = includeNews && selectedNews.length > 0;
    const hasLegalSelection = includeLegal && selectedLegal.length > 0;
    const hasDiligenceSelection =
      includeDiligence && selectedDiligence.length > 0;
    if (!hasNewsSelection && !hasLegalSelection && !hasDiligenceSelection) {
      setLoadCopySourceError("Select at least one item to copy.");
      return;
    }
    setLoadCopySourceError("");
    setIsCopySummaryStep(true);
  };

  const handleCopyInProgressChange = useCallback((inProgress: boolean) => {
    setIsCopyingItems(inProgress);
  }, []);

  const handleCopyFromReportSuccess = () => {
    setIsCopySummaryStep(false);
    clearCopyFromModalState();
    setShowCopyFromModal(false);
  };

  const handleCopyFromReportError = (message: string) => {
    setLoadCopySourceError(message);
  };

  const handleWorkbenchTabClick = (tab: WorkbenchTab) => {
    if (tab === activeWorkbenchTab) return;
    if (activeWorkbenchTab === "summary" && isSummaryEditing) {
      requestLeaveConfirmation(() => setActiveWorkbenchTab(tab));
      return;
    }
    setActiveWorkbenchTab(tab);
  };

  const handleTabStatusChange = useCallback(
    (tab: WorkbenchTab, status: string) => {
      setTabStatuses((prev) => {
        if (prev[tab] === status) return prev;
        return { ...prev, [tab]: status };
      });
    },
    []
  );

  const handleInvestigatorStatusChange = useCallback(
    (status: string) => handleTabStatusChange("investigator", status),
    [handleTabStatusChange]
  );

  const handleSummaryStatusChange = useCallback(
    (status: string) => handleTabStatusChange("summary", status),
    [handleTabStatusChange]
  );

  const handleWebSearchStatusChange = useCallback(
    (status: string) => handleTabStatusChange("webSearch", status),
    [handleTabStatusChange]
  );

  const handleCheckPDF = useCallback(
    (data: { report_uuid: string; exists: boolean }) => {
      if (data.exists) {
        setPdfExists(true);
        setIsPolling(false);
      }
    },
    []
  );

  useEffect(() => {
    const { id } = params;
    if (id) {
      setReportId(id);
      resetSubworkflowData();
      setIsReady(true);
    } else {
      console.error(
        "No report UUID found - components may not function correctly"
      );
      setIsReady(false);
    }
  }, [params, resetSubworkflowData]);

  // Get latest web search job for this report on mount/refresh
  const { data: latestJob, isLoading: isLoadingLatestJob } =
    useLatestWebSearchJobForReport(reportId || null, !!reportId);

  // Initialize webSearchRunExternalId from latest job if it exists
  // Set for running, partial (in progress), or completed jobs so the button shows correct state
  const latestJobRunExternalId = React.useMemo(
    () =>
      latestJob
        ? latestJob.run_external_id || latestJob.external_id || null
        : null,
    [latestJob]
  );

  const latestJobStatus = React.useMemo(
    () => (latestJob ? latestJob.status?.toLowerCase() || "" : ""),
    [latestJob]
  );

  useEffect(() => {
    if (latestJob === undefined) return;

    const shouldUseLatest =
      latestJobRunExternalId &&
      (latestJobStatus === "running" ||
        latestJobStatus === "partial" ||
        latestJobStatus === "completed");

    const nextValue = shouldUseLatest ? latestJobRunExternalId : null;

    setWebSearchRunExternalId((prev) =>
      prev === nextValue ? prev : nextValue
    );
  }, [latestJob, latestJobRunExternalId, latestJobStatus]);

  useEffect(() => {
    if (reportId) {
      reportDataGet(reportId);
    }
  }, [reportId, reportDataGet]);

  // Check PDF existence on page load
  useEffect(() => {
    if (reportId) {
      checkPDFRef.current(reportId, {
        onSuccess: handleCheckPDF,
      });
    }
  }, [reportId, handleCheckPDF]);

  useEffect(() => {
    if (!reportData || reportData.report_uuid !== reportId) return;
    setPersonUuid(reportData.person_uuid);
    subworkflowDataGet(reportData.workflow_run_uuid);
  }, [reportData, reportId, subworkflowDataGet, setPersonUuid]);

  useEffect(() => {
    if (!reportData) {
      setItemVisibility(null);
      return;
    }
    if (
      !reportData.blueprint_uuid ||
      !reportData.company_uuid ||
      !reportData.workflow_run_uuid
    ) {
      setItemVisibility(defaultItemVisibility);
      return;
    }
    setItemVisibility(null);
    getComputedBlueprint({
      blueprintUUID: reportData.blueprint_uuid,
      companyUUID: reportData.company_uuid,
      behaviorKitUUID: reportData.behavior_kit_uuid ?? "None",
      workflowRunUUID: reportData.workflow_run_uuid,
    });
  }, [getComputedBlueprint, reportData]);

  useEffect(() => {
    if (personUuid) {
      personExGet(personUuid);
      personAccountsGet(personUuid);
    }
  }, [personUuid, personExGet, personAccountsGet]);

  useEffect(() => {
    if (shouldOpenWebSearchTab) {
      setActiveWorkbenchTab("webSearch");
    }
  }, [shouldOpenWebSearchTab]);

  /** Clear person override when report or person changes (e.g. navigation). */
  useEffect(() => {
    setPersonOverride(null);
  }, [reportId, personUuid]);

  useEffect(() => {
    if (isComputedBlueprintLoading) return;
    if (computedBlueprint?.item_types?.length) {
      const enabledItems = new Set(
        computedBlueprint.item_types.map((item) => item.name)
      );
      setItemVisibility({
        showDiligence: enabledItems.has(ThreeSixtyItemTypes.DILIGENCE),
        showNews: enabledItems.has(ThreeSixtyItemTypes.NEWS),
        showLegal: enabledItems.has(ThreeSixtyItemTypes.LEGAL),
        showFamaReport: isAmendment360
          ? false
          : enabledItems.has(ThreeSixtyItemTypes.FAMA_REPORT),
      });
      return;
    }
    if (isComputedBlueprintError || isComputedBlueprintSuccess) {
      setItemVisibility(
        isAmendment360
          ? { ...defaultItemVisibility, showFamaReport: false }
          : defaultItemVisibility
      );
    }
  }, [
    computedBlueprint,
    isComputedBlueprintError,
    isComputedBlueprintLoading,
    isComputedBlueprintSuccess,
    isAmendment360,
  ]);

  // Polling effect for PDF check
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    let timeoutId: NodeJS.Timeout | undefined;

    if (isPolling && reportId) {
      // Delay the first check by 10 seconds, then poll every 5 seconds
      timeoutId = setTimeout(() => {
        checkPDFRef.current(reportId, {
          onSuccess: handleCheckPDF,
        });
        intervalId = setInterval(() => {
          checkPDFRef.current(reportId, {
            onSuccess: handleCheckPDF,
          });
        }, 5000); // subsequent checks every 5 seconds
      }, 10000); // initial delay of 10 seconds
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPolling, reportId, handleCheckPDF]);

  // Permission check based on mode (amendment 360 uses canNavAmendment)
  const hasPermission = isAmendment360
    ? canNavAmendment
    : isQAMode
    ? canEdit360Qa
    : canViewWIP360Reports;

  if (!hasPermission) {
    return (
      <div className="p-6">
        <div className="text-center">
          You do not have permission to view this page
        </div>
      </div>
    );
  }

  if (!isReady) {
    return (
      <div className="p-6">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <PreventLeaveProvider>
      <main className="flex-1 col-span-3 h-full block">
        <div className="p-x-6 print:p-0 h-full">
          <div className="max-w-8xl mx-auto h-full">
            <div className="flex flex-col h-full min-h-0">
              <div>
                <button
                  className="inline-flex justify-center items-center px-4 py-2 cursor-pointer gap-2 rounded-lg w-19 max-w-xs max-h-9 bg-transparent text-gray-600"
                  onClick={handleNavigateBack}
                >
                  <ChevronLeftIcon className="w-6" />
                  <label className="text-md font-medium pointer-events-none">
                    Back to List
                  </label>
                </button>
              </div>

              <div className="flex justify-between items-center pb-6 print:pb-0 print:m-0">
                <div className="flex flex-row items-center">
                  <span className="text-4xl leading-10 print:hidden font-extrabold">
                    {isAmendment360
                      ? "360 Amend"
                      : isQAMode
                      ? "Secondary QA"
                      : isWebSearchWorkbenchEntryRoute
                      ? "360 Web Search Workbench"
                      : "Investigator Workbench"}
                  </span>
                </div>
                <div className="print:hidden flex justify-items-end items-center space-x-2">
                  <div className="ml-4" data-testid="regenerate-pdf-button">
                    <button
                      className={`inline-flex justify-center items-center px-4 py-2 gap-2 rounded-lg w-19 max-w-xs max-h-9 border ${
                        pdfExists && !isGeneratingPDF && !isPolling
                          ? "bg-white text-gray-600 hover:bg-gray-50 cursor-pointer"
                          : "bg-white text-gray-400 border cursor-not-allowed"
                      }`}
                      onClick={handleRegeneratePDF}
                      disabled={!pdfExists || isGeneratingPDF || isPolling}
                    >
                      <ArrowPathIcon className="-ml-1 mr-1 h-4 w-4" />
                      <label className="text-sm leading-5 font-medium pointer-events-none">
                        Regenerate Sample PDF
                      </label>
                    </button>
                  </div>
                  <div className="ml-4" data-testid="generate-pdf-button">
                    <button
                      className={`inline-flex justify-center items-center px-4 py-2 cursor-pointer gap-2 rounded-lg w-19 max-w-xs max-h-9 border ${
                        isGeneratingPDF || isPolling
                          ? "bg-green-600 text-white cursor-not-allowed"
                          : "bg-green-600 text-white hover:bg-green-700"
                      }`}
                      onClick={handleGeneratePDF}
                      disabled={isGeneratingPDF || isPolling}
                    >
                      {isGeneratingPDF || isPolling ? (
                        <svg
                          className="animate-spin -ml-1 mr-1 h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : !pdfExists ? (
                        <ArrowPathIcon className="-ml-1 mr-1 h-4 w-4" />
                      ) : null}
                      <label className="text-sm leading-5 font-medium pointer-events-none">
                        {isGeneratingPDF || isPolling
                          ? "Generating..."
                          : pdfExists
                          ? "View Sample PDF"
                          : "Generate Sample PDF"}
                      </label>
                    </button>
                  </div>
                </div>
              </div>
              <div className="shrink-0 bg-red-600 text-white py-2 px-4 rounded-lg mb-4 text-center">
                REMINDER: Please make sure you start the timer before doing
                investigating work!
              </div>
              {canAddNotes && companyNote && (
                <div className="shrink-0 bg-yellow-100 flex flex-start text-gray-700 rounded-md text-sm leading-5 font-medium mb-4 p-4 items-start gap-4 min-w-0">
                  <span className="flex-shrink-0 inline-flex" aria-hidden>
                    <AddNoteIcon />
                  </span>
                  <span
                    className={classNames(
                      "min-w-0 flex-1",
                      LONG_TEXT_WRAP_CLASSES
                    )}
                  >
                    {companyNote}
                  </span>
                </div>
              )}
              {showRerunBanner && reportData && (
                <RerunReportBanner
                  report={reportData}
                  reportReruns={reportReruns}
                  variant="workbench"
                />
              )}
              <div className="flex flex-1 min-h-0 overflow-auto bg-white rounded-xl shadow shadow-gray-300 print:shadow-none">
                <div className="p-6 print:p-0 w-full">
                  <div className="flex flex-1 gap-6 w-full min-h-0">
                    <div className="w-[272px] shrink-0">
                      {reportDataLoading ||
                      personExLoading ||
                      personAccountsLoading ||
                      (personUuid ? personConsentFormsLoading : false) ? (
                        <LoadingSpinner />
                      ) : !!reportData && personEx && personAccounts ? (
                        <ReportPersonInfoSidebar360
                          data={reportData}
                          personEx={personOverride ?? personEx}
                          accounts={personAccounts}
                          canViewReportLogs={canAddNotes}
                          canView360={canView360}
                          canEdit360Reports={canEdit360Reports}
                          blueprintColor={computedBlueprint?.color ?? null}
                          consentForms={personConsentForms ?? []}
                          onPersonSaved={setPersonOverride}
                          onPersonSaveError={() => {
                            /* Do not clear personOverride on save error; clearing would
                               change personEx and reset the open form, wiping unsaved edits. */
                          }}
                        />
                      ) : (
                        <ExclamationTriangleIcon className="w-12" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col">
                        <div className="flex items-center justify-between border-b border-gray-200">
                          <div
                            role="tablist"
                            aria-label="Workbench tabs"
                            className="flex gap-7"
                          >
                            {workbenchTabs.map((tab) => {
                              const isActive = tab.id === activeWorkbenchTab;
                              return (
                                <button
                                  key={tab.id}
                                  role="tab"
                                  aria-selected={isActive}
                                  className={`justify-start text-lg py-2 font-noto-sans ${
                                    isActive
                                      ? "text-ourGreen border-ourGreen border-b-2 font-semibold"
                                      : "text-gray-400 border-gray-400 font-medium"
                                  }`}
                                  onClick={() =>
                                    handleWorkbenchTabClick(tab.id)
                                  }
                                >
                                  {tab.label}
                                </button>
                              );
                            })}
                          </div>
                          <div className="text-sm text-gray-500 min-h-[1.5rem]">
                            {tabStatuses[activeWorkbenchTab]}
                          </div>
                        </div>
                        <div className="flex flex-col pt-4">
                          {reportId && (
                            <>
                              <div
                                className={
                                  activeWorkbenchTab === "investigator"
                                    ? "block"
                                    : "hidden"
                                }
                              >
                                <Reports360NotesAutosave
                                  reportId={reportId}
                                  onStatusChange={
                                    handleInvestigatorStatusChange
                                  }
                                  showStatusInline={false}
                                />
                              </div>
                              <div
                                className={
                                  activeWorkbenchTab === "summary"
                                    ? "block"
                                    : "hidden"
                                }
                              >
                                <Reports360SummaryEditor
                                  ref={summaryEditorRef}
                                  workflow_run_uuid={subworkflow360}
                                  reportId={reportId}
                                  onEditingChange={setIsSummaryEditing}
                                  onStatusChange={handleSummaryStatusChange}
                                  showStatusInline={false}
                                />
                              </div>
                              <div
                                className={
                                  activeWorkbenchTab === "webSearch"
                                    ? "block"
                                    : "hidden"
                                }
                              >
                                <BooleanSearchTab
                                  reportId={reportId}
                                  runExternalId={webSearchRunExternalId}
                                  isInitializing={isLoadingLatestJob}
                                  completionAction={
                                    isOnWebSearchStep ? (
                                      <WebSearchDoneButton
                                        reportId={reportId}
                                        workflowRunUuid={subworkflow360}
                                      />
                                    ) : undefined
                                  }
                                  onStatusChange={handleWebSearchStatusChange}
                                  onSubmitSuccess={(runExternalId) => {
                                    if (runExternalId) {
                                      setWebSearchRunExternalId(runExternalId);
                                    }
                                    reportDataGet(reportId);
                                  }}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Report Items */}
                    <div className="flex flex-col gap-4 w-[320px] shrink-0">
                      <div className="font-semibold text-2xl flex">
                        Report Items
                        {canEdit360Reports &&
                          (copyFromSections.includeNews ||
                            copyFromSections.includeLegal ||
                            copyFromSections.includeDiligence) && (
                            <button
                              type="button"
                              className="ml-auto"
                              onClick={() => {
                                setIsCopySummaryStep(false);
                                setShowCopyFromModal(true);
                              }}
                            >
                              <CopyFromIcon className="w-4 h-4" />
                            </button>
                          )}
                      </div>
                      {reportId && personUuid && (
                        <>
                          {itemVisibility ? (
                            <>
                              {itemVisibility.showNews && (
                                <News
                                  report_uuid={reportId}
                                  person_uuid={personUuid}
                                  workflow_run_uuid={subworkflow360}
                                />
                              )}
                              {itemVisibility.showLegal &&
                                !isWebSearchWorkbenchEntryRoute && (
                                  <LegalCase
                                    report_uuid={reportId}
                                    workflow_run_uuid={subworkflow360}
                                  />
                                )}
                              {itemVisibility.showDiligence &&
                                v3DiligenceEnabled &&
                                !isWebSearchWorkbenchEntryRoute && (
                                  <Diligence
                                    report_uuid={reportId}
                                    workflow_run_uuid={subworkflow360}
                                  />
                                )}
                              {itemVisibility.showFamaReport && (
                                <MisconductLink report_uuid={reportId} />
                              )}
                            </>
                          ) : (
                            <div className="py-6 flex justify-center">
                              <LoadingSpinner />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {leaveConfirmationModal}
      <WorkbenchClaimPromptDialog
        isOpen={isClaimPromptOpen}
        isClaiming={isClaiming}
        onAction={handleClaimPromptAction}
      />
      <CopyFromReportModal
        open={showCopyFromModal && canEdit360Reports}
        onClose={handleCloseCopyFromModal}
        isSummaryStep={isCopySummaryStep}
        showNewsSection={copyFromSections.includeNews}
        showLegalSection={copyFromSections.includeLegal}
        showDiligenceSection={copyFromSections.includeDiligence}
        sourceReportUuid={sourceReportUuid}
        sourceNewsItems={sourceNewsItems}
        sourceLegalItems={sourceLegalItems}
        sourceDiligenceItems={sourceDiligenceItems}
        selectedNewsItems={selectedNews}
        selectedLegalItems={selectedLegal}
        selectedDiligenceItems={selectedDiligence}
        selectedNewsIds={selectedNewsIds}
        selectedLegalIds={selectedLegalIds}
        selectedDiligenceIds={selectedDiligenceIds}
        loadCopySourceError={loadCopySourceError}
        isLoadingCopySource={isLoadingCopySource}
        isCopyingItems={isCopyingItems}
        destinationReportUuid={reportId}
        onSourceReportUuidChange={handleSourceReportIdChange}
        onLoadSource={handleLoadCopySource}
        onToggleNews={toggleSelectedNews}
        onToggleLegal={toggleSelectedLegal}
        onToggleDiligence={toggleSelectedDiligence}
        onSelectAllNews={selectAllNews}
        onUnselectAllNews={unselectAllNews}
        onSelectAllLegal={selectAllLegal}
        onUnselectAllLegal={unselectAllLegal}
        onSelectAllDiligence={selectAllDiligence}
        onUnselectAllDiligence={unselectAllDiligence}
        onProceedToSummary={handleProceedToCopySummary}
        onBackToSelection={() => setIsCopySummaryStep(false)}
        onClearInformation={clearCopyFromModalState}
        onCopyInProgressChange={handleCopyInProgressChange}
        onCopySuccess={handleCopyFromReportSuccess}
        onCopyError={handleCopyFromReportError}
      />
    </PreventLeaveProvider>
  );
};

export default Workbench360;
